// Domain validation and constants — pure logic shared by the API and the web app.
//
// Rule for this module: nothing here may depend on FormData, Next, the browser or
// Node. It compiles identically on both sides. The transport-bound helpers
// (`texto(FormData)`) and the Supabase Storage path regexes were deliberately
// left out — see the note at the end of the file.

import type { Category, EventRepeat, GameId, GameTheme, TimedGameId } from "./types.js";

export const VALID_CATEGORIES: Category[] = [
  "primeiros",
  "viagem",
  "conquista",
  "data",
  "momento",
];

export function isCategory(value: string): value is Category {
  return (VALID_CATEGORIES as string[]).includes(value);
}

export const VALID_THEMES: GameTheme[] = [
  "paises",
  "profissoes",
  "filmes",
  "musica",
  "nos",
  "lugares",
  "comida",
  "celebracoes",
  "hobbies",
  "marcas",
  "personagens",
  "desporto",
];

export function isTheme(value: string): value is GameTheme {
  return (VALID_THEMES as string[]).includes(value);
}

/**
 * Normalize a word for duplicate-checking and comparison: lower-case, trimmed,
 * accents stripped, inner whitespace collapsed. So "São Paulo" == "sao  paulo".
 */
export function normalizeWord(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // strip combining accent marks
    .toLowerCase()
    .trim()
    .replace(/\s+/g, " ");
}

/**
 * The comparison key: `normalizeWord` plus stripping *all* non-alphanumeric
 * characters (spaces, punctuation, "&", "-", etc.). So "Toy Story", "toystory"
 * and "toy-story" all collapse to "toystory". This is what makes guessing
 * forgiving of spacing/punctuation differences (IDEA-026).
 */
export function comparisonKey(value: string): string {
  return normalizeWord(value).replace(/[^a-z0-9]/g, "");
}

/** Levenshtein edit distance between two strings (insert/delete/substitute = 1). */
export function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;

  // Single-row DP — O(a·b) time, O(b) space; words here are tiny.
  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const current = [i];
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      current[j] = Math.min(
        current[j - 1] + 1, // insertion
        previous[j] + 1, // deletion
        previous[j - 1] + cost, // substitution
      );
    }
    previous = current;
  }
  return previous[b.length];
}

/**
 * Is `guess` an accepted answer for `target`?
 *
 * The line is drawn between **how you write it** and **which letters they are**.
 *
 * How you write it is never decisive: case, accents, spaces and punctuation all
 * fall away in `comparisonKey`, so "toystory" answers "Toy Story" and "coracao"
 * answers "Coração". Nobody should lose a round to a cedilla they could not be
 * bothered to type, and that is the part of IDEA-026 that was always doing the
 * real work.
 *
 * Which letters they are is decisive, exactly. GATA is not GATO and PIZA is not
 * PIZZA — those are different words, and accepting one for the other means the
 * round was scored on an answer nobody gave. Both used to pass: the rule carried
 * a one-edit slack for words of four letters or more.
 *
 * That slack made sense under the old rules, where a rejected guess cost one of
 * three lives and you had no idea how long the word was. Both reasons are gone
 * — the dashes show the shape and the count, and a retry costs the two seconds
 * it takes to type. What is left of a near-miss is `isNearMiss`, which says
 * "close" without saying "right".
 */
export function isMatch(guess: string, target: string): boolean {
  const a = comparisonKey(guess);
  const b = comparisonKey(target);
  if (!a || !b) return false;
  return a === b;
}

/**
 * Is `guess` a "so close!" near-miss for `target` — wrong, but only just?
 * Purely for friendlier UI feedback (it costs seconds, not a try). Within two
 * edits and not a hit, so it now catches everything the old slack used to wave
 * through: a missing letter (PIZA), an extra one, and a wrong one (GATA).
 */
export function isNearMiss(guess: string, target: string): boolean {
  const a = comparisonKey(guess);
  const b = comparisonKey(target);
  if (!a || !b || isMatch(guess, target)) return false;
  return editDistance(a, b) <= 2;
}

/**
 * How many words each person composes per round.
 *
 * Three until v3, when the clock arrived. One is what the timed shape actually
 * wants: a round becomes one word each, one time each, straight across — the
 * nearest thing to a shared Memória board this game can have. Three words also
 * meant three separate clocks to sit down with before a round could close,
 * which is a lot of ceremony for a game you play in a queue.
 */
export const WORDS_PER_PERSON = 1;

/**
 * What one hint adds to your time (IDEA-025 v3).
 *
 * Hints used to be free and tries were the scarce thing — three of them, then
 * the word was gone. Now it is the other way round: guess as often as you like,
 * but a hint costs ten seconds against the clock that decides the round. It
 * keeps time the single deciding number, so the emoji game scores through the
 * same function as the two board games.
 */
export const HINT_PENALTY_MS = 10_000;

/**
 * What a hint costs, per game.
 *
 * One price for all of them was always going to be wrong for one, because the
 * games are not the same size. A hint in the emoji game uncovers one letter of
 * one word you solve in well under a minute — ten seconds is a real bite.
 *
 * A hint in a sudoku fills one of about twenty empty cells, on a board that
 * takes minutes. At ten seconds each you can fill the whole grid for a little
 * over three minutes and beat someone who actually solved it, which turns the
 * assist into the fastest way to win. At thirty it costs ten minutes to hint
 * your way through — comfortably worse than playing.
 *
 * O Tango fica entre os dois, em vinte. Uma pista dele decide uma casa entre
 * dois símbolos e não entre seis, por isso vale menos informação do que a do
 * sudoku; mas as regras dele encadeiam — uma casa fechada obriga muitas vezes a
 * seguinte — e uma pista bem calhada abre meia fila. Vinte segundos põem o
 * preço acima do que ela poupa sem tornar o tabuleiro de trinta e seis casas
 * impagável a quem encalhou de verdade numa.
 *
 * The alternative was a cap on how many hints a board allows. Price is the
 * gentler tool: it never refuses you the hint you genuinely need at 3am, it
 * just makes leaning on them lose.
 */
export const HINT_PENALTY_BY_GAME: Record<GameId, number> = {
  emoji: HINT_PENALTY_MS,
  memoria: HINT_PENALTY_MS, // no hints in Memória; here so the record is total
  shikaku: HINT_PENALTY_MS,
  sudoku: 30_000,
  pinpoint: HINT_PENALTY_MS, // sem pistas no Pinpoint; aqui para o registo ficar total
  tango: 20_000,
  // Queens: uma pista põe uma coroa das oito. Ao preço base davam-se as oito
  // por oitenta segundos e ganhava-se a quem resolvesse o tabuleiro a sério —
  // o mesmo raciocínio que põe o sudoku nos 30s põe este lá também.
  queens: 30_000,
  /**
   * Wend: a pista revela uma PALAVRA INTEIRA — um quarto do tabuleiro de uma
   * vez. É a maior assistência da casa e por isso a mais cara.
   *
   * Não há meia-pista possível aqui: dezoito casas em quatro palavras não têm
   * nada de suficientemente pequeno para revelar, e revelar só a primeira letra
   * de uma delas não diz nada a quem não sabe qual é a palavra. Sobra o preço.
   *
   * A conta: três pistas resolvem um tabuleiro (a quarta palavra é o que sobra),
   * e um tabuleiro resolvido a sério leva-se um minuto ou dois. A sessenta
   * segundos cada, esse atalho custa três minutos — comfortavelmente pior do
   * que jogar, que é exactamente o que um preço de pista tem de garantir.
   */
  wend: 60_000,
  /**
   * Crossclimb: a pista dá **uma letra** de uma palavra de quatro. É a ajuda
   * mais pequena da casa, e por isso a mais barata a seguir ao preço base.
   *
   * A conta: vinte letras nos cinco degraus do meio. A quinze segundos cada,
   * escrever a escada inteira à pista custa cinco minutos — bem pior do que
   * jogar. Uma ou duas quando se encalha numa pista custam o que devem custar.
   */
  crossclimb: 15_000,
  /**
   * Zip: a pista anda **três casas** do caminho certo, das quarenta e nove.
   *
   * A conta é directa porque a ajuda é toda do mesmo tamanho: quarenta e nove
   * casas a três por pista são dezassete pistas para o tabuleiro inteiro. A
   * vinte segundos, esse atalho custa perto de seis minutos — e um tabuleiro
   * jogado a sério leva um ou dois. Fica o que um preço de pista tem de
   * garantir: pedir uma quando se encalha é barato, resolver o jogo à pista
   * perde sempre.
   *
   * Há uma segunda coisa que esta pista faz e que se paga aqui: quando o
   * caminho já saiu da solução, ela **recua até onde ele ainda estava certo**.
   * Isso é mais do que informação, é trabalho desfeito — e é por isso que não
   * está ao preço base.
   */
  zip: 20_000,
};

export function hintPenaltyMs(jogo: GameId): number {
  return HINT_PENALTY_BY_GAME[jogo] ?? HINT_PENALTY_MS;
}

/**
 * O que custa revelar uma linha inteira, por jogo.
 *
 * Só o Crossclimb tem duas ajudas de tamanhos diferentes, e essa é a boa ideia
 * que se trouxe do original: um botão só obriga toda a gente à mesma escalada —
 * ou se aguenta sem nada, ou se pede a resposta. Dois deixam escolher quanta
 * ajuda se quer, e deixam cobrar cada uma pelo que ela vale.
 *
 * Três pistas por 45s davam três das quatro letras de um degrau, e com três
 * letras a quarta já não é ajuda nenhuma — por isso revelar a linha custa
 * exactamente o mesmo. Quem lá chega paga o mesmo por um caminho ou pelo outro,
 * e o que muda é só quantos toques dá.
 */
export const REVEAL_PENALTY_BY_GAME: Partial<Record<GameId, number>> = {
  crossclimb: 45_000,
};

export function revealPenaltyMs(jogo: GameId): number {
  return REVEAL_PENALTY_BY_GAME[jogo] ?? 0;
}

/**
 * The longest a game may plausibly have taken: 6 hours.
 *
 * Not an anti-cheat measure — with two players on the honour system there is
 * nothing to cheat for. It catches a stuck timer (a tab left open overnight)
 * turning into a permanent, unbeatable "record" on the scoreboard. Shared by
 * all three games since v3, when the emoji game got a clock too.
 */
export const MAX_GAME_TIME_MS = 6 * 60 * 60 * 1000;

/** Shortest credible time. Below this, something submitted twice or misfired. */
export const MIN_GAME_TIME_MS = 1000;

/**
 * A word is resolved once it has been guessed.
 *
 * `revelada` still counts, but only for rounds played before v3: back then
 * three failed tries revealed the answer and closed the word unsolved. Hints
 * stop one letter short of the full answer now, so nothing new is ever revealed
 * without being guessed — and those old rows must still read as finished.
 */
export function isWordResolved(word: {
  acertou_na: number | null;
  revelada: boolean;
  desistiu?: boolean;
}): boolean {
  return word.acertou_na !== null || word.revelada || Boolean(word.desistiu);
}

/**
 * Is a round finished? Finished = both people composed their full quota AND
 * every word is resolved. With two players, "both" means two distinct authors
 * each at the quota.
 *
 * Pure, so the web app can render "finished" without asking the API.
 */
export function isRoundFinished(
  words: {
    autor_email: string;
    acertou_na: number | null;
    revelada: boolean;
    desistiu?: boolean;
  }[],
): boolean {
  const byAuthor = new Map<string, number>();
  for (const w of words) {
    byAuthor.set(w.autor_email, (byAuthor.get(w.autor_email) ?? 0) + 1);
  }
  const bothComposed =
    byAuthor.size >= 2 && [...byAuthor.values()].every((n) => n >= WORDS_PER_PERSON);
  if (!bothComposed) return false;
  return words.every(isWordResolved);
}

// ------------------------------------------------------------
//  Games on the shared board (IDEA-025 → IDEA-031/032)
// ------------------------------------------------------------

export const VALID_GAMES: GameId[] = [
  "emoji",
  "memoria",
  "shikaku",
  "sudoku",
  "pinpoint",
  "tango",
  "queens",
  "wend",
  "crossclimb",
  "zip",
];

export function isGameId(value: string): value is GameId {
  return (VALID_GAMES as string[]).includes(value);
}

/** The games where a seeded board is raced against the clock. */
export const TIMED_GAMES: TimedGameId[] = [
  "memoria",
  "shikaku",
  "sudoku",
  "pinpoint",
  "tango",
  "queens",
  "wend",
  "crossclimb",
  "zip",
];

export function isTimedGame(value: string): value is TimedGameId {
  return (TIMED_GAMES as string[]).includes(value);
}

/** Human labels, used in the scoreboard's per-game filter. */
export const GAME_LABELS: Record<GameId, string> = {
  emoji: "Jogo dos emojis",
  memoria: "Jogo da memória",
  shikaku: "Shikaku",
  sudoku: "Sudoku",
  pinpoint: "Pinpoint",
  tango: "Tango",
  queens: "Queens",
  wend: "Wend",
  crossclimb: "Crossclimb",
  zip: "Zip",
};

/**
 * Points for a finished round of a timed game.
 *
 * **One point to whoever was faster, nothing to the other.**
 *
 * It used to be 5 / 3 / 2, a spread built for the old emoji game where points
 * measured *how well* you did — 5 for a first-guess answer, 1 for a third. Once
 * every game came down to a single question, "who was faster?", a spread had
 * nothing left to express: second place out of two is not a partial success,
 * and a 5-versus-2 round said "you nearly won" about a race someone lost.
 *
 * One point makes the scoreboard say something you can read at a glance —
 * **how many rounds each of you has won** — across all three games at once.
 *
 * A dead heat gives both the point rather than neither: nobody was slower.
 * It needs a tie to the millisecond, so it will essentially never happen.
 */
export const TIMED_WIN_POINTS = 1;
export const TIMED_DRAW_POINTS = 1;
export const TIMED_RUNNER_UP_POINTS = 0;

/**
 * A timed result's time, with its hints paid for.
 *
 * The emoji game charged for hints from the start; Shikaku counted them and
 * charged nothing, which was a leftover from when a round was scored 5/3/2 by
 * placing rather than by the clock. Once the clock became the only thing that
 * decides, a free hint was a free win.
 *
 * The raw `tempo_ms` stays raw in the database and the penalty is applied here,
 * at scoring — one place to change the price, and the stored time keeps meaning
 * "how long the board actually took".
 *
 * `revelacoes` só existe no Crossclimb, que é o único com duas ajudas de
 * tamanhos diferentes. Nos outros vem nulo e a parcela é zero — o que mantém
 * esta função a ser uma só, em vez de haver um cálculo por jogo espalhado por
 * quem chama.
 */
export function timedResultTime(
  result: { tempo_ms: number; pistas: number | null; revelacoes?: number | null },
  jogo: GameId,
): number {
  return (
    result.tempo_ms +
    (result.pistas ?? 0) * hintPenaltyMs(jogo) +
    (result.revelacoes ?? 0) * revealPenaltyMs(jogo)
  );
}

/**
 * O número por que uma ronda se decide. Menor ganha.
 *
 * Não é sempre tempo. Quatro dos jogos são corridas e o número é o relógio com as
 * pistas pagas; o Pinpoint não tem relógio nenhum de propósito — premeia pensar
 * bem, não depressa — e o número dele é quantos palpites foram precisos.
 *
 * Ter isto numa função só é o que deixa `timedRoundPoints` continuar a ser uma
 * regra apenas ("menor ganha, empate empata, quem não acabou está fora") sem
 * saber a que jogo pertence o número.
 */
export function resultadoScore(
  result: {
    tempo_ms: number;
    pistas: number | null;
    revelacoes?: number | null;
    palpites?: number | null;
  },
  jogo: GameId,
): number {
  if (jogo === "pinpoint") {
    // Sem palpites gravados não há resposta: fica pior do que qualquer vitória.
    return result.palpites ?? Number.MAX_SAFE_INTEGER;
  }
  return timedResultTime(result, jogo);
}

/**
 * Splits the points for one finished round between the two results.
 *
 * Pure and shared so the API awards exactly what the UI promises. Rounds with
 * fewer than two results score nothing — the round isn't over yet.
 *
 * Compares `timedResultTime`, so hints are paid for here rather than by every
 * caller remembering to add them.
 */
export function timedRoundPoints<
  T extends {
    jogador_email: string;
    tempo_ms: number;
    pistas?: number | null;
    revelacoes?: number | null;
    palpites?: number | null;
    desistiu?: boolean;
  },
>(results: T[], jogo: GameId): { email: string; points: number }[] {
  if (results.length < 2) return [];

  const tempo = (r: T) =>
    resultadoScore(
      {
        tempo_ms: r.tempo_ms,
        pistas: r.pistas ?? 0,
        revelacoes: r.revelacoes,
        palpites: r.palpites,
      },
      jogo,
    );

  // Walking away loses, whatever the clock said. Ranked out of the race rather
  // than given a huge time: a forfeit is not a slow attempt, and storing it as
  // one would let a forfeit beat someone who was genuinely slower.
  const jogou = results.filter((r) => !r.desistiu);
  if (jogou.length === 0) {
    return results.map((r) => ({ email: r.jogador_email, points: TIMED_RUNNER_UP_POINTS }));
  }

  const fastest = Math.min(...jogou.map(tempo));
  const drawn = jogou.filter((r) => tempo(r) === fastest).length > 1;

  return results.map((result) => ({
    email: result.jogador_email,
    points: result.desistiu
      ? TIMED_RUNNER_UP_POINTS
      : tempo(result) === fastest
        ? drawn
          ? TIMED_DRAW_POINTS
          : TIMED_WIN_POINTS
        : TIMED_RUNNER_UP_POINTS,
  }));
}

/** How many people have to finish before a timed round counts as over. */
export const PLAYERS_PER_ROUND = 2;

// ------------------------------------------------------------
//  Jogo da Memória (IDEA-031)
// ------------------------------------------------------------

/** The board is 6×6 — 36 cards, 18 pairs. */
export const MEMORY_GRID = 6;
export const MEMORY_CARDS = MEMORY_GRID * MEMORY_GRID;
export const MEMORY_PAIRS = MEMORY_CARDS / 2;

/**
 * The faces a pair can wear.
 *
 * Emoji rather than our own photographs, for v1: 18 distinct pairs would need
 * 18 good album photos to exist and to load before the timer is fair, and a
 * thumbnail shrunk to a card is hard to tell apart at a glance. Emoji are
 * instant, always available, and unmistakable. Photos are the obvious v2.
 *
 * There are more than 18 here on purpose — each round picks 18 of them, so two
 * rounds don't just reshuffle the same faces.
 */
export const MEMORY_FACES = [
  "💛", "🌙", "⭐", "🌻", "🍓", "🐝", "🦋", "🌊", "🔥", "🎈",
  "🍒", "🌸", "🎁", "🕯️", "🧁", "☕", "🎵", "📖", "🧸", "🌈",
  "🍑", "🐧", "🌵", "🍀", "🪐", "🎠", "🫧", "🍦",
] as const;

// ------------------------------------------------------------
//  Events — the shared calendar (IDEA-030)
// ------------------------------------------------------------

export const VALID_REPEATS: EventRepeat[] = ["nunca", "anual"];

export function isEventRepeat(value: string): value is EventRepeat {
  return (VALID_REPEATS as string[]).includes(value);
}

/** "HH:MM" or "HH:MM:SS" — what a `time` column accepts. */
export function isClockTime(value: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/.test(value);
}

/** Trims a time to "HH:MM" — Postgres hands back "20:00:00". */
export function toShortTime(value: string | null): string | null {
  return value ? value.slice(0, 5) : null;
}

export const EVENT_TITLE_MAX = 120;
export const PLACE_NAME_MAX = 120;

// ------------------------------------------------------------
//  Uploads — limits shared by the client and the server.
//
//  These live here on purpose: if the client and the API keep separate copies,
//  sooner or later they drift and an upload passes in the browser only to be
//  rejected by the API (or worse, the other way round). One source, one place.
//  (STACK.md §9)
// ------------------------------------------------------------

/** Image types accepted for upload. */
export const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"] as const;

export type ImageType = (typeof IMAGE_TYPES)[number];

export function isImageType(value: string): value is ImageType {
  return (IMAGE_TYPES as readonly string[]).includes(value);
}

/** What can be uploaded: an album photo or a storybook illustration. */
export type UploadKind = "foto" | "ilustracao";

/** Maximum size per kind, in bytes. */
export const MAX_SIZE: Record<UploadKind, number> = {
  foto: 10 * 1024 * 1024, // 10 MB
  ilustracao: 15 * 1024 * 1024, // 15 MB
};

/** The bucket prefix (folder) for each kind. The key itself is generated by the API. */
export const UPLOAD_PREFIX: Record<UploadKind, string> = {
  foto: "album",
  ilustracao: "ilustracoes",
};

/** File extension to use for each accepted content type. */
export const EXTENSION_BY_TYPE: Record<ImageType, string> = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
};

// ------------------------------------------------------------
//  Redirect destinations — open-redirect guard.
// ------------------------------------------------------------

/**
 * Only accepts internal paths ("/album", "/timeline"). Rejects absolute URLs and
 * "//host" (which browsers treat as protocol-relative and follow off-site).
 * Came from the login page's `destinoSeguro()`; it lives here because both the
 * web app and the API need the same rule.
 */
export function safeDestination(value: string | null | undefined, fallback = "/"): string {
  if (!value) return fallback;
  if (!value.startsWith("/") || value.startsWith("//")) return fallback;
  return value;
}

// ------------------------------------------------------------
//  A note on what is NOT here
// ------------------------------------------------------------
//  `texto(formData, key)` — bound to FormData; the API takes JSON validated with
//  zod, so it never crosses the boundary. It stays in `lib/validacao.ts` for as
//  long as the Next app exists.
//
//  `caminhoFotoValido()` / `caminhoIlustracaoValido()` — these existed because the
//  object key was generated on the *client*, leaving the server able only to
//  regex-check it. From phase 5 the API generates the key itself (STACK.md §9),
//  so the regex has no job left: there is no externally-supplied path to validate.
